/**
 * MypagePresentsContainer
 * --
 */
import React, { useState } from 'react';
import MypagePresentsPresenter from './MypagePresentsPresenter';
import dayjs from 'dayjs';
import {
  useCancelPresent,
  useGetPresents,
  useUpdatePresent,
} from 'hooks/PresentHooks';
import { useUserStack } from 'store';
import MessageAlert from 'utils';

const MypagePresentsContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { navigate, screenSize } = props;

  /* ===== Mutate ===== */
  const { updatePresent } = useUpdatePresent();
  const { cancelPresent } = useCancelPresent();

  /* ===== Store ===== */
  const userInfo = useUserStack();

  /* ===== Data ===== */
  const { data: Presents } = useGetPresents(
    {
      user_id: userInfo?.user_id,
      target_id: userInfo?.user_id,
      type: 'PRODUCT',
    },
    {
      enabled: !!userInfo?.user_id,
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
      initialData: [],
    }
  );

  /* ===== Hooks ===== */
  /* ===== STATE ===== */
  const [selectedTab, setSelectedTab] = useState('GIVE'); // 기본값 설정
  const [currentPage, setCurrentPage] = useState(1); // 현재 페이지 상태 추가
  const [isLoading, setIsLoading] = React.useState(false);

  // Period Date
  const today = React.useMemo(() => dayjs(), []);
  const [selectedDate, setSelectedDate] = React.useState([
    today.subtract(1, 'month').startOf('day'),
    today.endOf('day'),
  ]);
  const [dataList, setDataList] = React.useState([]);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 선물 연장하기
   * --
   * @param {object} obj
   */
  const handleExtension = async (obj) => {
    if (!obj) return;

    const confirm = window.confirm(
      `${obj?.product?.present_information?.present_expired_date}일 연장하시겠습니까?`
    );

    if (!confirm) return;

    try {
      setIsLoading(true);

      const exp = dayjs
        .unix(obj.expired_date)
        .add(obj?.product?.present_information?.present_expired_date, 'day')
        .unix();

      const { status } = await updatePresent({
        present_id: obj?.present_id,
        expired_date: exp,
        extendable_count: obj.extendable_count - 1,
      });

      if (status !== 200) {
        MessageAlert.info('연장에 실패하였습니다.');
        return;
      }

      MessageAlert.success('선물 연장에 성공하였습니다.');
    } catch (err) {
      console.error('[handleExtension] Error!! ', err);
      MessageAlert.error('선물 기간 연장에 실패하였습니다.');
    } finally {
      setTimeout(() => setIsLoading(false), 1000);
    }
  };

  /**
   * 선물하기 결제 취소
   * --
   * @param {number} idx
   */
  const handleCancel = async (idx = null) => {
    if (!idx) {
      MessageAlert.info('선물을 찾을 수 없습니다.');
      return;
    }

    const confirm = window.confirm(
      '주문을 취소하시겠습니까?\n여러 개를 구매/선물하였을 경우 모든 쿠폰이 취소되며, 이미 사용한 쿠폰인 경우 취소 요청이 제한됩니다.'
    );

    if (!confirm) return;

    try {
      const { status, data } = await cancelPresent({
        present_id: idx,
      });

      if (status !== 200) {
        MessageAlert.info('선물 결제 취소에 실패하였습니다.');
        return;
      }
      const newData = dataList.map((_item) =>
        _item.present_id !== idx
          ? _item
          : {
              ..._item,
              isCancelAble: false,
              payment_state: 'CANCELLED',
              cancel_payment_info: data?.cancel_payment_info ?? null,
            }
      );
      setDataList(newData);
      MessageAlert.success('결제가 취소되었습니다!');
    } catch (err) {
      console.error('[handleCancel] Error!! ', err);
      MessageAlert.error('선물 결제 취소에 실패하였습니다.');
    } finally {
      setTimeout(() => setIsLoading(false), 1000);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 선물 데이터 정제
   * --
   */
  const PresentList = React.useMemo(() => {
    const newList = dataList?.reduce(
      (acc, cur) => {
        const { user_id, target_id } = cur;
        if (user_id === target_id) {
          acc['GIVE'].push(cur);
          acc['RECEIVE'].push(cur);
        } else if (user_id === userInfo?.user_id) {
          acc['GIVE'].push(cur);
        } else if (target_id === userInfo?.user_id) {
          acc['RECEIVE'].push(cur);
        }

        return acc;
      },
      { GIVE: [], RECEIVE: [] }
    );

    return newList;
  }, [dataList, userInfo]);

  /**
   * 기간별 선물 데이터 추출
   * --
   */
  React.useEffect(() => {
    if (!Presents.length) return;

    setIsLoading(true);
    const filterData = Presents.filter(
      (present) =>
        present?.created_at >= dayjs(selectedDate[0]).startOf('day').unix() &&
        present?.created_at <= dayjs(selectedDate[1]).endOf('day').unix()
    );

    const newData = filterData.map((_item) => {
      const create = dayjs.unix(_item?.created_at);

      // NOTE: 취소 가능 여부
      const isCancelAble =
        _item?.used_present === 0 &&
        today.diff(create, 'day') <= 7 &&
        _item?.user_id === userInfo?.user_id &&
        _item?.payment_state === 'COMPLETED'
          ? true
          : false;

      return {
        ..._item,
        isCancelAble: isCancelAble,
      };
    });

    setTimeout(() => {
      setDataList(newData);
      setIsLoading(false);
    }, 1000);
  }, [selectedDate, Presents]);

  /* ===== RENDER ===== */
  return (
    <>
      <MypagePresentsPresenter
        navigate={navigate}
        screenSize={screenSize}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage} // setCurrentPage 전달
        data={PresentList}
        onExtension={handleExtension}
        isLoading={isLoading}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        today={today}
        onCancel={handleCancel}
      />
    </>
  );
};

export default MypagePresentsContainer;
